// @flow
import * as React from "react";
import { connect } from "react-redux";
import * as rt from "../../../../lib/SimulationReportModels.tsx";
import { SET_ORDER_TABLE_PRODUCTION_MODAL } from "../reducers/common.tsx";
import { LinesOrdersLogic, PropertiesLogic } from "../reducers/BusinessLogic.tsx";
import OrderTableProductionModal from "../../OrderTableProductionModal.tsx";
import { GanttChartSource } from "../reducers/properties.tsx";
import { addHours, dateFromWeekAndShift, TIME_RANGES } from "../../../../lib/Util";
import { LinesOrdersMaps } from "../reducers/Mappers.tsx";
import { LINE_GROUP_TAGS_ACCESS, ORDER_TAGS, ORDER_TAGS_ACCESS } from "../../../../lib/ManufacturingTags.generated";
import { getLineGroupTags } from "../../../../lib/BusinessLogic";

import type { GanttChartSourceTypes } from "../reducers/properties.tsx";
import type { SetOrderTableProductionModalAction } from "../reducers/common.tsx";
import type { ReduxState } from "../reducers/reducers.tsx";
import type { IOrderModelBase } from "../../../../lib/backend/manufacturing2.generated.types";
import type { OperationDetailsOverrides } from "../../OrderTableProductionModal.tsx";
import type { LineOrders } from "../reducers/linesOrders.tsx";

import type {
    ISimulationReportOrder,
    ISimulationReportUnscheduledOrder,
} from "../../../../lib/backend/reports.generated.types";

type Props = {
    show_modal: boolean,
    order_process_num: string,
    order_external_id: string,
    selected_order_uuid: string,
    reduxDispatch: (args: SetOrderTableProductionModalAction) => void,
    onOrderChange?: (order: IOrderModelBase) => void,
    source_type: GanttChartSourceTypes,
    report_uuid?: string,
    report: rt.ISimulationReportEx,
    lines_orders: LineOrders[],
    unscheduled_orders: LineOrders[],
    line_group_uuid: string,
};

type State = {};

const prepareOperationDetailsOverrides = (
    order_external_id: string,
    lines_orders: LineOrders[],
    unscheduled_orders: LineOrders[],
    report: rt.ISimulationReportEx,
    is_converted_order: boolean,
    unscheduled_align_earliest_end: boolean
): Map<string, OperationDetailsOverrides> => {
    const operations: {
        quantity_total: number,
        skip_sim: boolean,
        line_uuid: string,
        line_external_id: string,
        line_title: string,
        uuid: string,
        external_id: string,
        external_operation_id: string,
        earliest_start: number,
        earliest_end: number,
        ignore_on_planning_board?: boolean,
    }[] = [];

    for (const lines_orders_group of [lines_orders, unscheduled_orders]) {
        for (const line of lines_orders_group) {
            for (const order of line.orders) {
                if (order.external_id === order_external_id) {
                    operations.push({
                        quantity_total: order.quantity_total,
                        skip_sim: lines_orders_group === unscheduled_orders,
                        line_uuid: line.line_uuid,
                        line_external_id: line.line_external_id,
                        line_title: line.line_title,
                        uuid: order.uuid,
                        external_id: order.external_id,
                        external_operation_id: order.external_operation_id,
                        earliest_start: order.earliest_start,
                        earliest_end: order.earliest_end,
                        ignore_on_planning_board:
                            order.tags[ORDER_TAGS.ignore_on_planning_board] !== undefined
                                ? ORDER_TAGS_ACCESS.ignore_on_planning_board(order.tags)
                                : undefined,
                    });
                }
            }
        }
    }

    for (const operation of LinesOrdersMaps.converted_extra_operations.values()) {
        if (operation.external_id === order_external_id) {
            operations.push({
                quantity_total: operation.quantity_total,
                skip_sim: operation.skip_sim,
                line_uuid: operation.line.uuid,
                line_external_id: operation.line.external_id,
                line_title: operation.line.title,
                uuid: operation.uuid,
                external_id: operation.external_id,
                external_operation_id: operation.external_operation_id,
                earliest_start: operation.earliest_start,
                earliest_end: operation.earliest_end,
                ignore_on_planning_board:
                    operation.tags[ORDER_TAGS.ignore_on_planning_board] !== undefined
                        ? ORDER_TAGS_ACCESS.ignore_on_planning_board(operation.tags)
                        : undefined,
            });
        }
    }

    const scheduled_operations_map: Map<string, ISimulationReportOrder> = new Map();
    if (report.orders) {
        for (const order of report.orders) {
            for (const operation of order.production) {
                scheduled_operations_map.set(operation.order_id, operation);
            }
        }
    }
    const unscheduled_operations_map: Map<string, ISimulationReportUnscheduledOrder> = new Map(
        (report.unscheduled || []).map((operation: ISimulationReportUnscheduledOrder) => [
            operation.order_uuid,
            operation,
        ])
    );
    const from_shift_time = report.from_shift_time;
    const start_shift_date = dateFromWeekAndShift(
        from_shift_time.week,
        from_shift_time.year,
        from_shift_time.shift_number
    );
    const operations_details_overrides: Map<string, OperationDetailsOverrides> = new Map();
    for (const operation of operations) {
        const operation_details_overrides: OperationDetailsOverrides = {
            quantity_total: operation.quantity_total,
            skip_sim: operation.skip_sim,
            line_uuid: operation.line_uuid,
            line_external_id: operation.line_external_id,
            line_title: operation.line_title,
            uuid: operation.uuid,
            external_id: operation.external_id,
            external_operation_id: operation.external_operation_id,
        };

        if (operation.ignore_on_planning_board !== undefined) {
            operation_details_overrides.ignore_on_planning_board = operation.ignore_on_planning_board;
        }

        let time_start: Date | void;
        let time_end: Date | void;
        if (!operation.skip_sim) {
            const scheduled_operation = scheduled_operations_map.get(operation.uuid);
            if (scheduled_operation) {
                time_start = addHours(start_shift_date, scheduled_operation.estimated_start[1].val);
                time_end = addHours(start_shift_date, scheduled_operation.estimated_completion[1].val);
            }
        } else {
            const unscheduled_operation = unscheduled_operations_map.get(operation.uuid);
            if (unscheduled_operation) {
                const estimated_duration = unscheduled_operation.estimated_duration[1].val;
                const start_hour =
                    Math.max(
                        0,
                        unscheduled_align_earliest_end
                            ? operation.earliest_end -
                                  estimated_duration * TIME_RANGES.HOUR -
                                  start_shift_date.getTime()
                            : operation.earliest_start - start_shift_date.getTime()
                    ) / TIME_RANGES.HOUR;
                time_start = addHours(start_shift_date, start_hour);
                time_end = addHours(start_shift_date, start_hour + estimated_duration);
            }
        }

        if (time_start !== undefined) {
            operation_details_overrides.time_start = time_start.getTime();
        }

        if (time_end !== undefined) {
            operation_details_overrides.time_end = time_end.getTime();
        }

        const uuid = is_converted_order
            ? LinesOrdersLogic.getConvertedOperationParentId(operation.uuid)
            : operation.uuid;
        operations_details_overrides.set(uuid, operation_details_overrides);
    }

    return operations_details_overrides;
};

class ShowGanttOrderDataOnClick extends React.Component<Props, State> {
    ref = null;

    constructor(props: Props) {
        super(props);
        this.ref = React.createRef();
    }

    onCloseModal = () => {
        this.props.reduxDispatch({
            type: SET_ORDER_TABLE_PRODUCTION_MODAL,
            data: {
                show_modal: false,
                order_external_id: "",
                order_uuid: "",
                order_process_num: "",
            },
        });
    };

    render() {
        if (!this.props.show_modal) {
            return null;
        }

        let order_external_id = this.props.order_external_id;
        let selected_order_uuid = this.props.selected_order_uuid;
        const is_converted_order = LinesOrdersLogic.isConvertedOperation(this.props.selected_order_uuid);
        if (is_converted_order) {
            const parent_operation = LinesOrdersLogic.getConvertedOperationParent(this.props.selected_order_uuid);
            if (parent_operation) {
                order_external_id = parent_operation.external_id;
                selected_order_uuid = parent_operation.uuid;
            }
        }

        const line_group_tags = getLineGroupTags(this.props.line_group_uuid);
        const unscheduled_align_earliest_end = LINE_GROUP_TAGS_ACCESS.backward_scheduling(line_group_tags);

        return (
            <div ref={this.ref}>
                <OrderTableProductionModal
                    show={this.props.show_modal}
                    selected_order_uuid={selected_order_uuid}
                    selected_order_external_id={order_external_id}
                    process_num={this.props.order_process_num}
                    onShowOrHideOrderModal={this.onCloseModal}
                    onOrderSave={this.props.onOrderChange}
                    show_edit_button={
                        this.props.source_type === GanttChartSource.gantt ||
                        PropertiesLogic.isPlanningTable(this.props.source_type)
                    }
                    override_description_fields={[
                        "external_id",
                        "material_external_id",
                        "material_title",
                        "quantity_total",
                    ]}
                    show_columns={[
                        "process_num",
                        "title",
                        "quantity_produced",
                        "quantity_scrap",
                        "quantity_total",
                        "output_stock",
                        "status",
                        "time_start",
                        "time_end",
                        "skip_sim",
                        "ignore_on_planning_board",
                    ]}
                    report_uuid={this.props.report_uuid}
                    report={this.props.report}
                    operation_details_overrides={prepareOperationDetailsOverrides(
                        this.props.order_external_id,
                        this.props.lines_orders,
                        this.props.unscheduled_orders,
                        this.props.report,
                        is_converted_order,
                        unscheduled_align_earliest_end
                    )}
                />
            </div>
        );
    }
}

export default connect(
    (state: ReduxState) => {
        const modal = state.standalone_gantt.order_table_production_modal;
        const source_type = state.gantt_chart_properties.source_type;
        const is_planning_table =
            source_type === GanttChartSource.planning_table || source_type === GanttChartSource.planning_table_parallel;
        let report_uuid;
        if (is_planning_table && state.gantt_chart_report.report_data !== null) {
            report_uuid = state.gantt_chart_report.report_data.uuid;
        }

        const lines_orders = state.gantt_chart_lines_orders.lines_orders;
        const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;
        const line_group_uuid = state.gantt_chart_properties.line_group_uuid || "";

        return {
            show_modal: modal.show_modal,
            order_external_id: modal.order_external_id,
            selected_order_uuid: modal.order_uuid,
            order_process_num: modal.order_process_num,
            source_type,
            report_uuid,
            lines_orders,
            unscheduled_orders,
            line_group_uuid,
        };
    },
    dispatch => ({ reduxDispatch: dispatch })
)(ShowGanttOrderDataOnClick);
